/* eslint-disable no-console */
/**
 * Environment utilities — mode detection and startup validation
 */

export const isDevelopment = (): boolean =>
  import.meta.env.DEV || import.meta.env.MODE === 'development';

export const isProduction = (): boolean =>
  import.meta.env.PROD || import.meta.env.MODE === 'production';

export const getEnvironment = (): 'development' | 'production' | 'test' => {
  if (import.meta.env.MODE === 'test') return 'test';
  if (isProduction()) return 'production';
  return 'development';
};

interface EnvValidationResult {
  valid: boolean;
  missing: string[];
}

/**
 * Validate all required frontend environment variables.
 * Call at application startup and fail loud if anything is missing.
 */
export const validateEnvironment = (): EnvValidationResult => {
  const required: Record<string, string | undefined> = {
    VITE_CLERK_PUBLISHABLE_KEY: import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
    VITE_SUPABASE_PROJECT_ID:   import.meta.env.VITE_SUPABASE_PROJECT_ID,
    VITE_SUPABASE_ANON_KEY:     import.meta.env.VITE_SUPABASE_ANON_KEY,
    VITE_API_BASE_URL:          import.meta.env.VITE_API_BASE_URL,
  };

  const missing = Object.entries(required)
    .filter(([, value]) => !value)
    .map(([key]) => key);

  if (missing.length > 0) {
    const message =
      `Missing required environment variables: ${missing.join(', ')}\n` +
      `Copy .env.example to .env and fill in the values.`;
    throw new Error(message);
  }

  return { valid: true, missing: [] };
};

/**
 * Log a summary of the current environment (development only).
 */
export const logEnvironmentInfo = (): void => {
  if (!isDevelopment()) return;

  const clerkKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;
  const isDevKey = clerkKey?.startsWith('pk_test_');

  console.log(
    '%c🔧 Development Environment',
    'background: #06B6D4; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;'
  );
  if (isDevKey) {
    console.log('%c✓ Using Clerk development keys', 'color: #10B981; font-weight: 500;');
  }
};
